import type { PaginatedResponse } from "@/features/cle-compliance/models/courseCertificate";
import type {
  ReductionEvidenceListModel,
  ReductionEvidenceModel,
  ReductionQueryParams,
} from "@/features/cle-compliance/models/reduction";
import { del, get, getBlob, postForm } from "./util";

export const REDUCTIONS_QUERY_KEY = ["reductions"] as const;

export const REDUCTIONS_LIST_QUERY_KEY = [
  ...REDUCTIONS_QUERY_KEY,
  "list",
] as const;

export const createReductionDetailQueryKey = (id: string) =>
  [...REDUCTIONS_QUERY_KEY, "detail", id] as const;

export const createReductionFileQueryKey = (id: string) =>
  [...REDUCTIONS_QUERY_KEY, "files", id] as const;

const REDUCTIONS_QUERY = "documents/reductions";

const createReductionDownloadQuery = (id: string): string =>
  `${REDUCTIONS_QUERY}/${id}/download`;

const createReductionsForReportingQuery = (reportId: string): string =>
  `${REDUCTIONS_QUERY}/available-for-period/${reportId}`;

const createReductionsForInjunctionQuery = (injunctionId: string): string =>
  `${REDUCTIONS_QUERY}/available-for-injunction/${injunctionId}`;

const createReductionsForImpositionQuery = (impositionId: string): string =>
  `${REDUCTIONS_QUERY}/available-for-imposition/${impositionId}`;

const createSearchParams = ({
  page,
  page_size,
  reported,
}: ReductionQueryParams): string => {
  const searchParams = new URLSearchParams();

  if (reported != null) {
    searchParams.set("reported", String(reported));
  }

  if (page != null) {
    searchParams.set("page", String(page));
  }

  if (page_size != null) {
    searchParams.set("page_size", String(page_size));
  }

  return searchParams.toString();
};

const createListQuery = (params: ReductionQueryParams = {}): string => {
  const queryString = createSearchParams(params);

  return queryString ? `${REDUCTIONS_QUERY}?${queryString}` : REDUCTIONS_QUERY;
};

export type CreateReductionRequest = {
  file: File;
  numberOfLessons: number;
};

export const fetchReductions = (
  params: ReductionQueryParams = {},
): Promise<PaginatedResponse<ReductionEvidenceListModel>> =>
  get<PaginatedResponse<ReductionEvidenceListModel>>({
    query: createListQuery(params),
  });

export const fetchReductionsForReporting = (
  reportId: string,
): Promise<ReductionEvidenceListModel[]> =>
  get<ReductionEvidenceListModel[]>({
    query: createReductionsForReportingQuery(reportId),
  });

export const fetchReductionsForInjunction = (
  injunctionId: string,
): Promise<ReductionEvidenceListModel[]> =>
  get<ReductionEvidenceListModel[]>({
    query: createReductionsForInjunctionQuery(injunctionId),
  });

export const fetchReductionsForImposition = (
  impositionId: string,
): Promise<ReductionEvidenceListModel[]> =>
  get<ReductionEvidenceListModel[]>({
    query: createReductionsForImpositionQuery(impositionId),
  });

export const fetchReductionById = (
  id: string,
): Promise<ReductionEvidenceModel> =>
  get<ReductionEvidenceModel>({
    query: `${REDUCTIONS_QUERY}/${id}`,
  });

export const createReduction = ({
  file,
  numberOfLessons,
}: CreateReductionRequest): Promise<ReductionEvidenceModel> => {
  const formData = new FormData();

  formData.append("file", file);

  return postForm<ReductionEvidenceModel>({
    query: `${REDUCTIONS_QUERY}?numberOfLessons=${numberOfLessons}`,
    body: formData,
  });
};

export const fetchReductionDocumentFile = (id: string): Promise<Blob> =>
  getBlob({
    query: createReductionDownloadQuery(id),
  });

export const deleteReduction = async (id: string): Promise<void> => {
  await del({
    query: `${REDUCTIONS_QUERY}/${id}`,
  });
};
